import type { PricingMatrixQuery, UpsertOverridePayload } from "./client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  deactivateOverride,
  getCompareByVariant,
  getPricingMatrix,
  upsertOverride,
} from "./client";

export const pricingKeys = {
  all: ["pricing"] as const,
  matrix: (q: PricingMatrixQuery) => ["pricing", "matrix", q] as const,
  compareByVariant: (variantId: string) =>
    ["pricing", "compareByVariant", variantId] as const,
};

export function usePricingMatrixQuery(query: PricingMatrixQuery) {
  return useQuery({
    queryKey: pricingKeys.matrix(query),
    queryFn: () => getPricingMatrix(query),
    enabled:
      Array.isArray(query.branchIds) && query.branchIds.length > 0,
  });
}

export function useCompareByVariantQuery(variantId: string | null) {
  return useQuery({
    queryKey: pricingKeys.compareByVariant(variantId ?? ""),
    queryFn: () => getCompareByVariant(variantId!),
    enabled: !!variantId,
  });
}

export function useUpsertOverrideMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpsertOverridePayload) => upsertOverride(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pricingKeys.all });
    },
  });
}

export function useDeactivateOverrideMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      branchId,
      variantId,
    }: {
      branchId: string;
      variantId: string;
    }) => deactivateOverride(branchId, variantId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pricingKeys.all });
    },
  });
}
